import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { CompanyProfile, fetchPublicCompanyProfile } from '@/services/companyProfile';

const fallbackCompanyProfile: Pick<CompanyProfile, 'servicePhone' | 'servicePhoneNote' | 'serviceHours'> = {
  servicePhone: '0800-123-456',
  servicePhoneNote: '免付費',
  serviceHours: '週一至週五 09:00 - 18:00',
};

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay * 80}ms` }}
    >
      {children}
    </div>
  );
}

const nextSteps = [
  {
    icon: 'ri-phone-line',
    title: '專人聯繫確認',
    desc: '我們將於 1-2 個工作天內致電與您確認檢測時間與注意事項。',
  },
  {
    icon: 'ri-file-list-3-line',
    title: '檢測前準備',
    desc: '檢測前請避免暴露於高分貝噪音環境，以確保檢測結果準確。',
  },
  {
    icon: 'ri-gift-line',
    title: '專屬優惠享不完',
    desc: '完成檢測後，可享助聽器選配折扣與延長保固服務。',
  },
];

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();

  const name = searchParams.get('name') || '';
  const phone = searchParams.get('phone') || '';
  const date = searchParams.get('date') || '';
  const time = searchParams.get('time') || '';
  const store = searchParams.get('store') || '';
  const description = searchParams.get('description') || '';
  const bookingType = 'ProductTrial';
  const productName = searchParams.get('productName') || '';
  const productCode = searchParams.get('productCode') || '';

  const [companyProfile, setCompanyProfile] = useState(fallbackCompanyProfile);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    let alive = true;
    fetchPublicCompanyProfile().then((profile) => {
      if (alive && profile) setCompanyProfile(profile);
    });
    return () => {
      alive = false;
    };
  }, []);

  const formatDisplayDate = (d: string) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${y} 年 ${m} 月 ${day} 日`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* Hero */}
        <div className="relative overflow-hidden text-white pt-4 md:pt-6 pb-8 md:pb-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E40] via-[#256F4A] to-[#1B3D2A]" />
          <div className="absolute -top-20 -right-20 w-72 h-72 md:w-96 md:h-96 rounded-full bg-[#D4A574]/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full bg-[#2E8B57]/20 blur-2xl" />

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
              預約完成
            </span>
            <h1 className="text-3xl md:text-4xl font-bold">預約成功！</h1>
            <p className="mt-3 text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              感謝您的預約，我們已收到您的資料，將盡快與您聯繫確認。
            </p>
          </div>

          <svg className="absolute bottom-0 left-0 w-full h-auto block" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>

        {/* Main Content */}
        <div className="px-6 md:px-10 lg:px-16 py-6 md:py-8 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-12">
            {/* Left - Summary */}
            <div className="lg:w-5/12 flex flex-col gap-4 md:gap-5">
              <SectionReveal>
                <div>
                  <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                    預約摘要
                  </span>
                  <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">您的預約資訊</h2>
                  <div className="w-12 h-1 bg-[#D4A574] mt-3 rounded-full" />
                </div>
              </SectionReveal>

              <SectionReveal>
                <div className="bg-white rounded-2xl border border-[#E8E0D6] p-4 md:p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                      <i className="ri-calendar-line text-[#1B3D2A]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#9A8A7A]">預約類型</p>
                      <p className="text-sm font-bold text-[#1B3D2A]">{bookingType === 'ProductTrial' ? '商品試戴' : '聽力檢測'}</p>
                    </div>
                  </div>
                  {productName && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <i className="ri-hearing-line text-[#1B3D2A]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#9A8A7A]">來源商品</p>
                        <p className="text-sm font-bold text-[#1B3D2A]">{productName}</p>
                        {productCode && <p className="text-xs text-[#9A8A7A]">產品編號：{productCode}</p>}
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                      <i className="ri-calendar-line text-[#1B3D2A]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#9A8A7A]">預約日期</p>
                      <p className="text-sm font-bold text-[#1B3D2A]">{formatDisplayDate(date)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-[#1B3D2A]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#9A8A7A]">預約時段</p>
                      <p className="text-sm font-bold text-[#1B3D2A]">{time}</p>
                    </div>
                  </div>
                  <div className="w-full h-px bg-[#E8E0D6]" />
                  {store && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <i className="ri-map-pin-line text-[#1B3D2A]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#9A8A7A]">希望前往門市</p>
                        <p className="text-sm font-bold text-[#1B3D2A]">{store}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                      <i className="ri-user-line text-[#1B3D2A]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#9A8A7A]">姓名</p>
                      <p className="text-sm font-bold text-[#1B3D2A]">{name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-[#1B3D2A]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#9A8A7A]">聯絡電話</p>
                      <p className="text-sm font-bold text-[#1B3D2A]">{phone}</p>
                    </div>
                  </div>
                  {description && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <i className="ri-file-text-line text-[#1B3D2A]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#9A8A7A]">問題描述</p>
                        <p className="text-sm text-[#5A6B5A] leading-relaxed">{description}</p>
                      </div>
                    </div>
                  )}
                </div>
              </SectionReveal>
            </div>

            {/* Right - Next Steps */}
            <div className="lg:w-7/12">
              <div className="bg-white rounded-2xl border border-[#E8E0D6] p-4 md:p-6 lg:p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-3xl text-[#1B3D2A]" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">預約已確認送出</h2>
                  <p className="mt-2 text-sm text-[#5A6B5A]">
                    我們已收到您的預約資料，接下來將為您安排專業聽力檢測。
                  </p>
                </div>

                <div className="space-y-3">
                  {nextSteps.map((item, idx) => (
                    <SectionReveal key={item.title} delay={idx}>
                      <div className="flex items-start gap-3 p-3 rounded-xl bg-[#FAF8F5] border border-[#E8E0D6]/60">
                        <div className="w-10 h-10 rounded-xl bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                          <i className={`${item.icon} text-[#1B3D2A]`} />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-[#1B3D2A]">{item.title}</h3>
                          <p className="text-sm text-[#5A6B5A] mt-0.5 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </SectionReveal>
                  ))}
                </div>

                <div className="mt-6 space-y-3">
                  <Link
                    to="/"
                    className="block w-full bg-[#1B3D2A] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors text-center"
                  >
                    返回首頁
                  </Link>
                  <Link
                    to="/knowledge"
                    className="block w-full px-6 py-3 rounded-xl text-sm font-bold border border-[#E8E0D6] text-[#5A6B5A] hover:bg-[#FAF8F5] transition-colors text-center"
                  >
                    瀏覽聽力知識
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-[#FAF8F5] py-6 md:py-8">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionReveal>
              <div className="text-center mb-5 md:mb-6">
                <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
                  需要協助
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">聯絡我們</h2>
                <div className="w-12 h-1 bg-[#D4A574] mx-auto mt-3 rounded-full" />
              </div>
            </SectionReveal>

            <SectionReveal>
              <div className="bg-white rounded-2xl border border-[#E8E0D6] p-4 md:p-6 text-center max-w-xl mx-auto">
                <div className="w-12 h-12 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center mx-auto">
                  <i className="ri-customer-service-2-line text-xl text-[#1B3D2A]" />
                </div>
                <h3 className="mt-3 text-sm font-bold text-[#1B3D2A]">客服專線</h3>
                <p className="mt-2 text-sm font-medium text-[#D4A574]">
                  {companyProfile.servicePhone}
                  {companyProfile.servicePhoneNote ? `（${companyProfile.servicePhoneNote}）` : ''}
                </p>
                <p className="mt-1 text-xs text-[#9A8A7A]">{companyProfile.serviceHours}</p>
                <p className="mt-3 text-sm text-[#5A6B5A]">
                  如有任何問題，歡迎隨時聯繫我們，我們將竭誠為您服務。
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
